import React, { FormEvent, useState } from "react";
import "./CustomerRegistration.css";

const API_URL = "/api/customers";

interface CustomerForm {
  name: string;
  phone: string;
}

interface FormErrors {
  name?: string;
  phone?: string;
}

interface ApiResponse {
  message?: string;
}

type Status = "idle" | "submitting" | "success" | "error";

export function CustomerRegistration(): JSX.Element {
  const [form, setForm] = useState<CustomerForm>({
    name: "",
    phone: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const { name, value } = event.target;

    const nextValue =
      name === "phone"
        ? value.replace(/\D/g, "").slice(0, 10)
        : value;

    setForm((current) => ({
      ...current,
      [name]: nextValue,
    }));

    setErrors((current) => ({
      ...current,
      [name]: undefined,
    }));

    setMessage("");
  };

  const validate = (): boolean => {
    const nextErrors: FormErrors = {};

    if (!form.name.trim()) {
      nextErrors.name = "Please enter your name";
    } else if (form.name.trim().length < 2) {
      nextErrors.name = "Please enter a valid name";
    }

    if (!form.phone) {
      nextErrors.phone = "Please enter your mobile number";
    } else if (!/^[6-9]\d{9}$/.test(form.phone)) {
      nextErrors.phone = "Enter a valid 10-digit mobile number";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>
  ): Promise<void> => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone,
        }),
      });

      const data: ApiResponse | null = await response
        .json()
        .catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.message || "Unable to register. Please try again."
        );
      }

      setStatus("success");
      setMessage(
        data?.message || "Your details have been registered successfully."
      );

      setForm({
        name: "",
        phone: "",
      });
    } catch (error: unknown) {
      setStatus("error");

      setMessage(
        error instanceof Error
          ? error.message
          : "Something went wrong. Please try again."
      );
    }
  };

  if (status === "success") {
    return (
      <div className="customer-registration-page">
        <div className="customer-registration-card customer-registration-success">
          <div className="customer-registration-success-icon">
            ✓
          </div>

          <h1>Thank You!</h1>

          <p>{message}</p>

          <button
            type="button"
            className="customer-registration-secondary-button"
            onClick={() => {
              setStatus("idle");
              setMessage("");
            }}
          >
            Register Another Customer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-registration-page">
      <div className="customer-registration-card">
        <div className="customer-registration-header">
          <div className="customer-registration-logo">
            RP
          </div>

          <h1>Stay Connected With Us</h1>

          <p>Enter your details to register with us.</p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="customer-registration-field">
            <label htmlFor="customer-name">Your Name</label>

            <input
              id="customer-name"
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              placeholder="Enter your name"
              autoComplete="name"
              maxLength={100}
              autoFocus
            />

            {errors.name && (
              <span className="customer-registration-error">
                {errors.name}
              </span>
            )}
          </div>

          <div className="customer-registration-field">
            <label htmlFor="customer-phone">Mobile Number</label>

            <div className="customer-registration-phone">
              <span className="customer-registration-country-code">
                +91
              </span>

              <input
                id="customer-phone"
                name="phone"
                type="tel"
                inputMode="numeric"
                value={form.phone}
                onChange={handleChange}
                placeholder="10-digit mobile number"
                autoComplete="tel"
                maxLength={10}
              />
            </div>

            {errors.phone && (
              <span className="customer-registration-error">
                {errors.phone}
              </span>
            )}
          </div>

          {status === "error" && (
            <div className="customer-registration-api-error">
              {message}
            </div>
          )}

          <button
            type="submit"
            className="customer-registration-submit"
            disabled={status === "submitting"}
          >
            {status === "submitting"
              ? "Registering..."
              : "Register"}
          </button>
        </form>

        <p className="customer-registration-privacy">
          Your details are used only to maintain your customer record.
        </p>
      </div>
    </div>
  );
}
